/**
 * Self-Service Feed Builder — the public flow (PRD §4, §5).
 *
 * Three steps and a preview. Unauthenticated (NAT-02) but explicitly NOT a
 * separate product (NAT-01): Alchemiq's palette, type and terminology
 * throughout.
 *
 * The visual language follows the alchemiqfeed reference — navy bar, numbered
 * worksheet sections, cyan step accents, orange for actions and selection. The
 * FIELDS are this codebase's, not the reference's: the reference asks for
 * several recipients and WhatsApp/link delivery, which §2.2 explicitly excludes
 * from the MVP, so those are not reproduced. Nor is its "Saved automatically"
 * header — §12 requires removing exactly that claim.
 *
 * §5.3 shapes this file: the user never sees a category, keyword, threshold,
 * retrieval volume or prompt text. Everything technical is decided server-side
 * and returned only as the plain-language interpretation, so this component has
 * nothing technical to leak even by accident.
 *
 * §12 — answers live in sessionStorage only. No server-side draft recovery.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../app/http-common';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import './FeedBuilder.css';

const STORAGE_KEY = 'alchemiq.feedBuilder.answers';
const DRAFT_TOKEN_KEY = 'alchemiq.feedBuilder.draftToken';
const TOTAL_STEPS = 3;
/** spec v8 #1/#3 — the opening screen sits before step 1 and is not one of the three. */
const STEP_INTRO = -1;
const AUTOSAVE_DEBOUNCE_MS = 1000;

const EMPTY_ANSWERS = {
  // spec v8 #10 — "Brief context", still stored as `organization`: renaming the
  // field would orphan every existing submission and the admin views that read it.
  organization: '',
  // Adi review — several outcomes may apply at once.
  purposes: [],
  // spec v8 #16/#17 — required when 'other' is among the purposes.
  purposeOther: '',
  // Adi review — background material typed rather than uploaded.
  backgroundNotes: '',
  whatToFind: '',
  whatToLeaveOut: '',
  relevantWebsite: '',
  recipientEmail: '',
  geographicCoverage: '',
  resultLanguage: 'English',
  frequency: 'weekdays',
  // Adi review — the schedule is the client's. Blank means "not chosen yet"; the
  // timezone is filled from the browser once the options load.
  scheduleTimeZone: '',
  scheduleHour: 8,
  workWeek: '',
  feedName: '',
};

/** Same-tab refresh cache. The server copy, reached by draft token, is authoritative. */
function loadStoredAnswers() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? { ...EMPTY_ANSWERS, ...JSON.parse(raw) } : EMPTY_ANSWERS;
  } catch {
    return EMPTY_ANSWERS;
  }
}

/**
 * localStorage, not sessionStorage: the point of a resume token is that closing
 * the tab does not lose the draft. Read from the URL first so a copied resume
 * link works on another device (spec v8 #54).
 */
function loadDraftToken() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('draft');
    if (fromUrl) {
      localStorage.setItem(DRAFT_TOKEN_KEY, fromUrl);
      // Out of the address bar immediately: it is a credential, and it would
      // otherwise reach history, screenshots and any Referer we send.
      window.history.replaceState({}, '', window.location.pathname);
      return fromUrl;
    }
    return localStorage.getItem(DRAFT_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

/**
 * Zones whose working week runs Sunday–Thursday. Mirrors the server's list and
 * is only a PRE-SELECTION — the visitor can change it, so a wrong guess costs a
 * click rather than a wrong schedule.
 */
const SUN_THU_ZONES = new Set([
  'Asia/Jerusalem', 'Asia/Tel_Aviv', 'Asia/Gaza', 'Asia/Hebron',
  'Asia/Amman', 'Asia/Damascus', 'Asia/Baghdad', 'Asia/Kuwait',
  'Asia/Riyadh', 'Asia/Qatar', 'Asia/Bahrain', 'Africa/Cairo',
  'Africa/Tripoli', 'Asia/Aden',
]);

const defaultWorkWeekFor = (tz) => (SUN_THU_ZONES.has(String(tz || '')) ? 'sun_thu' : 'mon_fri');

/** The browser's own zone, so most visitors never touch the control. */
function detectTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * Fields WE pre-fill, not the visitor. The schedule is seeded from the browser on
 * load, so counting it as content would make a first-time visitor look like they
 * had a draft in progress — the intro would say "Continue my news feed", and
 * autosave would create a submission row for every page load.
 */
const AUTO_FILLED_FIELDS = new Set(['scheduleTimeZone', 'scheduleHour', 'workWeek']);

const hasAnyAnswer = (a) => Object.keys(EMPTY_ANSWERS)
  .filter((k) => !AUTO_FILLED_FIELDS.has(k))
  .some((k) => {
    const v = a[k];
    if (Array.isArray(v)) return v.length > 0;
    return String(v ?? '') !== String(EMPTY_ANSWERS[k]);
  });

const isEmail = (v) => /^[^@\s,;]+@[^@.\s,;]+(\.[^@.\s,;]+)+$/.test(String(v || '').trim());

/**
 * The page is its own scroll container inside the fixed-viewport app shell, so
 * `window.scrollTo` does nothing here — the element has to be scrolled.
 */
function scrollPageToTop() {
  const page = document.querySelector('.sfb-page');
  if (page) page.scrollTo({ top: 0, behavior: 'smooth' });
}

export default function FeedBuilderPage() {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(STEP_INTRO);
  const [answers, setAnswers] = useState(loadStoredAnswers);
  const [errors, setErrors] = useState({});
  const [submissionId, setSubmissionId] = useState(null);
  const [draftToken, setDraftToken] = useState(null);
  // 'idle' | 'saving' | 'saved' | 'error' — drives the top-bar indicator (#52/#53).
  const [saveState, setSaveState] = useState('idle');
  const [resumedAt, setResumedAt] = useState(null);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [interpretation, setInterpretation] = useState(null);
  const [activating, setActivating] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [activated, setActivated] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  // §5.1 — the optional background PDF.
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getFeedBuilderOptions();
        if (cancelled) return;
        setOptions(res);
        // Pre-fill the schedule from the browser. Only fills blanks, so a resumed
        // draft keeps whatever the visitor chose.
        setAnswers((a) => {
          if (a.scheduleTimeZone) return a;
          const detected = detectTimeZone();
          if (!detected) return a;
          const supported = (res?.timeZones || []).some((z) => z.id === detected);
          if (!supported) return a;
          // The working week is deliberately NOT seeded here. It is derived from
          // the zone at render, so a visitor who corrects their timezone gets the
          // matching week; the moment they pick one themselves it becomes sticky.
          return { ...a, scheduleTimeZone: detected };
        });
      } catch {
        if (!cancelled) setOptions({ enabled: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Refs, not state: the save loop must read the LATEST answers without being a
  // dependency of itself.
  const answersRef = useRef(answers);
  const draftRef = useRef({ submissionId: null, draftToken: null });
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const timerRef = useRef(null);
  const hydratedRef = useRef(false);
  const generationRef = useRef(0);

  useEffect(() => { answersRef.current = answers; }, [answers]);

  /**
   * One save at a time, with a trailing re-send if the answers moved while a
   * request was out.
   *
   * Single-flight rather than sequence numbers because the server applies
   * `Object.assign(existing, patch)` on whatever fields a body contains — two
   * overlapping requests would let a stale snapshot clobber newer values, and
   * the loser would be whichever happened to land second.
   */
  const flushSave = useCallback(async () => {
    if (inFlightRef.current) { pendingRef.current = true; return draftRef.current; }
    const snapshot = answersRef.current;
    if (!hasAnyAnswer(snapshot)) return draftRef.current;

    const generation = generationRef.current;
    inFlightRef.current = true;
    setSaveState('saving');
    try {
      const res = await api.saveFeedBuilderSubmission(
        draftRef.current.submissionId,
        snapshot,
        // Silent: a failed autosave must not throw the app-wide error modal over
        // a public page. The indicator reports it instead.
        { silent: true },
        draftRef.current.draftToken,
      );
      // A "Start a new feed" during the request invalidates its result — without
      // this the abandoned token is re-attached to the fresh worksheet.
      if (generation !== generationRef.current) return draftRef.current;

      draftRef.current = { submissionId: res.submissionId, draftToken: res.draftToken };
      setSubmissionId(res.submissionId);
      setDraftToken(res.draftToken);
      try {
        if (res.draftToken) localStorage.setItem(DRAFT_TOKEN_KEY, res.draftToken);
      } catch { /* private mode — the draft still lives server-side */ }
      setSaveState('saved');
      return draftRef.current;
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'SELF_SERVICE_EMPTY_DRAFT') {
        setSaveState('idle');
        return draftRef.current;
      }
      if (code === 'SELF_SERVICE_NOT_FOUND') {
        // The draft we were told about is gone. Forget it and let the next
        // keystroke create a fresh one rather than retrying forever.
        draftRef.current = { submissionId: null, draftToken: null };
        try { localStorage.removeItem(DRAFT_TOKEN_KEY); } catch { /* ignore */ }
      }
      setSaveState('error');
      return draftRef.current;
    } finally {
      inFlightRef.current = false;
      if (pendingRef.current && generation === generationRef.current) {
        pendingRef.current = false;
        flushSave();
      }
    }
  }, []);

  // spec v8 #51 — save about a second after the last change. The sessionStorage
  // write stays immediate: it costs nothing and is the offline fallback.
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers)); } catch { /* quota — not fatal */ }
    // Skip the change fired by hydration, or a resumed draft immediately POSTs
    // its own answers back and a fresh visitor creates an empty row on load.
    if (!hydratedRef.current) return undefined;
    if (!hasAnyAnswer(answers)) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flushSave(); }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [answers, flushSave]);

  /**
   * spec v8 #54 — reopen an unfinished draft on arrival.
   *
   * The server copy wins: the token names it, and sessionStorage is only a
   * same-tab cache. If the fetch fails we keep whatever was cached locally and
   * carry on — autosave re-syncs.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = loadDraftToken();
      if (!token) { hydratedRef.current = true; return; }
      try {
        const draft = await api.getFeedBuilderDraft(token);
        if (cancelled) return;
        if (draft?.resumable) {
          draftRef.current = { submissionId: draft.submissionId, draftToken: draft.draftToken };
          setSubmissionId(draft.submissionId);
          setDraftToken(draft.draftToken);
          setAnswers({ ...EMPTY_ANSWERS, ...draft.answers });
          if (draft.file) setUploadedFile({ name: draft.file.filename });
          setResumedAt(draft.updatedAt || null);
          setSaveState('saved');
        } else {
          // Already activated, or aged out. Start clean rather than showing a
          // form that the server would refuse to save.
          try { localStorage.removeItem(DRAFT_TOKEN_KEY); } catch { /* ignore */ }
        }
      } catch { /* offline or expired — fall back to the local cache */ }
      if (!cancelled) hydratedRef.current = true;
    })();
    return () => { cancelled = true; };
  }, []);

  /** spec v8 #55/#61 — an empty worksheet, and a retired draft behind it. */
  const startNewFeed = useCallback(async () => {
    generationRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingRef.current = false;
    const token = draftRef.current.draftToken;
    draftRef.current = { submissionId: null, draftToken: null };
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(DRAFT_TOKEN_KEY);
    } catch { /* ignore */ }
    setAnswers(EMPTY_ANSWERS);
    setErrors({});
    setSubmissionId(null);
    setDraftToken(null);
    setInterpretation(null);
    setUploadedFile(null);
    setActivated(null);
    setPreview(null);
    setSaveState('idle');
    setResumedAt(null);
    setConfirmRestart(false);
    setStep(STEP_INTRO);
    scrollPageToTop();
    // Retire the old token so an abandoned link stops resolving. Deliberately
    // not a delete: the row is admin context and abuse evidence.
    if (token) { try { await api.closeFeedBuilderDraft(token); } catch { /* best effort */ } }
  }, []);

  const requestStartNew = useCallback(() => {
    if (draftRef.current.draftToken || hasAnyAnswer(answersRef.current)) setConfirmRestart(true);
    else startNewFeed();
  }, [startNewFeed]);

  /** Add or remove one purpose, leaving the others alone. */
  const togglePurpose = useCallback((key) => {
    setAnswers((a) => {
      const has = a.purposes.includes(key);
      return { ...a, purposes: has ? a.purposes.filter((p) => p !== key) : [...a.purposes, key] };
    });
    setErrors((e) => (e.purpose ? { ...e, purpose: null } : e));
  }, []);

  const set = useCallback((field, value) => {
    setAnswers((a) => ({ ...a, [field]: value }));
    setErrors((e) => (e[field] ? { ...e, [field]: null } : e));
  }, []);

  const validateStep = useCallback((which) => {
    const next = {};
    if (which === 0) {
      if (!answers.organization.trim()) {
        next.organization = 'Please tell us what this feed is about.';
      }
      // A length check: an empty array is truthy, so `!answers.purposes` would
      // let someone continue having chosen nothing.
      if (answers.purposes.length === 0) {
        next.purpose = 'Please choose at least one thing this feed should help you do.';
      }
      // spec v8 #16 — "Something else" is only an answer once they say what it is.
      if (answers.purposes.includes('other') && !answers.purposeOther.trim()) {
        next.purposeOther = 'Please tell us what this feed should help you do.';
      }
      if (!answers.whatToFind.trim()) next.whatToFind = 'Please describe what Alchemiq should find.';
    }
    if (which === 1) {
      if (!isEmail(answers.recipientEmail)) next.recipientEmail = 'Please enter one valid email address.';
      if (!answers.frequency) next.frequency = 'Please choose how often you want it.';
    }
    // spec v8 #41 — a feed cannot be activated without a name. The name is
    // optional on page 1 and suggested here, so this is the only place it binds.
    if (which === 2 && !answers.feedName.trim()) {
      next.feedName = 'Please give your feed a name.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [answers]);

  /**
   * Persist server-side on every step change rather than only at the end: an
   * activation that fails still has to leave a submission behind for retry
   * (§9.1), and that is impossible if the answers never reached the server.
   */
  const saveAndAdvance = useCallback(async (nextStep) => {
    if (nextStep > step && !validateStep(step)) return;
    if (advancing) return;
    setFormError(null);
    setAdvancing(true);
    try {
      // Cancel the pending debounce and flush now, so the advance and the
      // autosave cannot race each other with different snapshots.
      if (timerRef.current) clearTimeout(timerRef.current);
      const res = await flushSave();
      const id = res.submissionId;
      if (!id) throw new Error('not-saved');
      if (nextStep === 2) {
        const interp = await api.getFeedBuilderInterpretation(id);
        setInterpretation(interp.interpretation);
        // The generated name is editable on the review screen only (GEN-08).
        if (!answers.feedName && interp.interpretation.feedName) {
          setAnswers((a) => ({ ...a, feedName: interp.interpretation.feedName }));
        }
      }
      setStep(nextStep);
      scrollPageToTop();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setAdvancing(false);
    }
  }, [answers.feedName, step, validateStep, flushSave, advancing]);

  const activate = useCallback(async () => {
    if (!validateStep(2)) return;
    setActivating(true);
    setFormError(null);
    try {
      // Flush so an edited feed name on the review screen is included, through
      // the same single-flight path as everything else.
      if (timerRef.current) clearTimeout(timerRef.current);
      const saved = await flushSave();
      // Silent: this page shows its own failure notice. The app-wide error modal
      // is the authenticated shell's, and it looks like a crash on a public page.
      const res = await api.activateFeedBuilderSubmission(
        saved.submissionId || submissionId, { silent: true },
      );
      setActivated(res);
      // The draft has become a feed; its token must stop resolving.
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(DRAFT_TOKEN_KEY);
      } catch { /* not fatal */ }
      scrollPageToTop();

      // §8 — the preview runs after activation and is allowed to be slow; the
      // feed is already live regardless of what it returns.
      setPreviewLoading(true);
      try {
        setPreview(await api.getFeedBuilderPreview(submissionId));
      } finally {
        setPreviewLoading(false);
      }
    } catch (err) {
      setFormError(err.response?.data?.error || 'We could not activate your feed. Please try again.');
    } finally {
      setActivating(false);
    }
  }, [submissionId, flushSave, validateStep]);

  /**
   * §5.1 — one optional PDF, extracted through the same path as the admin
   * wizard. The endpoint attaches it to a submission, so a visitor who uploads
   * before touching Continue needs one created first.
   */
  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setFormError(null);
    try {
      // Through flushSave, not a second POST: an upload racing an autosave used
      // to create two submissions and orphan one, taking the file with it.
      let id = draftRef.current.submissionId;
      if (!id) id = (await flushSave()).submissionId;
      if (!id) throw new Error('not-saved');
      const res = await api.uploadFeedBuilderFile(id, file);
      setUploadedFile({ name: res.filename, characters: res.extractedCharacters });
    } catch (err) {
      setFormError(
        err.response?.data?.error
        || 'We could not read that file. A PDF, DOCX or plain-text file under 10 MB works best.',
      );
    } finally {
      setUploading(false);
    }
  }, [flushSave]);

  const purposes = options?.purposes || [];
  const frequencies = options?.frequencies || [];
  const languages = options?.resultLanguages || ['English'];
  const timeZones = options?.timeZones || [];
  const workWeeks = options?.workWeeks || [];
  // Falls back rather than rendering blank while the options are still loading.
  const workWeek = answers.workWeek || defaultWorkWeekFor(answers.scheduleTimeZone);
  const selectedFrequency = frequencies.find((f) => f.key === answers.frequency) || null;

  // spec v8 #43/#47 — two groups, because each gets its own Edit action pointing
  // at the page that owns those answers.
  const monitorRows = useMemo(() => {
    if (!interpretation) return [];
    return [
      ['Primary purpose', interpretation.purpose],
      ['What Alchemiq will look for', interpretation.monitor],
      ['What Alchemiq will leave out', interpretation.leaveOut || 'Nothing specific'],
      ['Priority locations', interpretation.priorityGeography],
    ];
  }, [interpretation]);

  const deliveryRows = useMemo(() => {
    if (!interpretation) return [];
    return [
      ['Result language', interpretation.resultLanguage],
      ['Delivery schedule', interpretation.deliverySchedule],
      ['Recipient email', interpretation.recipientEmail],
    ];
  }, [interpretation]);

  if (loading) {
    return (
      <Shell>
        <div className="sfb-loading"><span className="sfb-spinner" />Loading…</div>
      </Shell>
    );
  }

  if (!options?.enabled) {
    return (
      <Shell>
        <div className="sfb-closed">
          The Alchemiq feed builder is not accepting new feeds right now.
          <br />Please check back later.
        </div>
      </Shell>
    );
  }

  // ── Activated: preview only (§8) ─────────────────────────────────────
  if (activated) {
    return (
      <Shell>
        <p className="sfb-eyebrow" style={{ marginBottom: 10 }}>Alchemiq global intelligence</p>
        <h1 className="sfb-title">Your news feed is ready</h1>
        <p className="sfb-lede">
          <strong style={{ fontWeight: 600 }}>{activated.feedName}</strong> has been created.
        </p>

        {/* spec v8 #57-59 named the recipient and the date of the first send.
            A feed now waits for an Alchemiq editor to approve it before it runs,
            so there IS no first-send date to give — and promising one we cannot
            honour is worse than saying plainly that someone is looking at it.
            The date, when there is one, is still formatted server-side so a
            browser locale cannot reintroduce a time or a timezone. */}
        <div className="sfb-success-banner">
          <CheckCircleOutlineIcon style={{ fontSize: 19, flexShrink: 0 }} />
          <span>
            {activated.pendingApproval ? (
              <>
                An Alchemiq editor is reviewing it.
                {activated.recipientEmail
                  ? <> Once it is approved, your updates will go to <strong>{activated.recipientEmail}</strong>, and we will email you when the first one is on its way.</>
                  : ' We will email you once it is approved.'}
              </>
            ) : (
              <>
                Your feed is active.
                {activated.recipientEmail && activated.firstDeliveryDate ? (
                  <> Your first update will be sent to <strong>{activated.recipientEmail}</strong> on <strong>{activated.firstDeliveryDate}</strong>.</>
                ) : ' Nothing else is needed from you.'}
              </>
            )}
          </span>
        </div>

        {previewLoading && (
          <div className="sfb-activating">
            <span className="sfb-spinner" />
            Putting together a few examples from the past seven days…
          </div>
        )}

        {!previewLoading && preview && (
          <>
            <p className="sfb-preview-label">{preview.label}</p>
            {preview.stories?.length > 0 ? (
              <div>
                {preview.stories.map((s, idx) => (
                  <div className="sfb-story" key={`${s.slug || 'story'}-${idx}`}>
                    <div className="sfb-story-index">{String(idx + 1).padStart(2, '0')}</div>
                    <div>
                      {/* NAT-05 — a headline opens Alchemiq's own public story
                          page, never the publisher, so the visitor stays inside
                          the product. Plain text when the story has no slug;
                          the preview stays read-only either way (PRE-06). */}
                      <h3 className="sfb-story-title">
                        {s.slug ? (
                          <a
                            className="sfb-story-link"
                            href={`/s/${s.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {s.title}
                          </a>
                        ) : s.title}
                      </h3>
                      {s.articleCount != null && (
                        <div className="sfb-story-meta">
                          <NewspaperOutlinedIcon style={{ fontSize: 13, verticalAlign: '-2px', marginRight: 5 }} />
                          {s.articleCount} {s.articleCount === 1 ? 'article' : 'articles'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // §14 — a live feed with no examples yet is a normal outcome, not a
              // failure, and must not read like one.
              <div className="sfb-empty-preview">
                {preview.previewFailed
                  ? preview.message
                  : 'We did not find matching stories in the past seven days. Your feed will email you as soon as it does.'}
              </div>
            )}
          </>
        )}

        <div className="sfb-actions">
          <button type="button" className="sfb-btn sfb-btn--secondary" onClick={startNewFeed}>
            Create another feed
          </button>
          <span />
        </div>
      </Shell>
    );
  }

  // ── Opening screen (spec v8 #1-3) ────────────────────────────────────
  // Rendered before step 1 and deliberately NOT counted as one of the three, so
  // it carries no step counter and no progress bar.
  if (step === STEP_INTRO) {
    return (
      <Shell wide>
        <div className="sfb-intro">
          <div className="sfb-intro-main">
            <p className="sfb-eyebrow">Alchemiq global intelligence</p>
            <h1 className="sfb-title sfb-title--hero">Build your personalised global news feed</h1>
            <p className="sfb-intro-lede">
              Tell us what matters to you and build a focused brief for Alchemiq.
            </p>
            <p className="sfb-intro-body">
              Create a personalised global news brief for your role, organisation or
              audience. Alchemiq searches across countries, languages and local
              sources to identify meaningful stories, emerging signals and
              opportunities — not just keyword matches.
            </p>
            <p className="sfb-intro-body">
              Answer a few questions and review your completed brief before deciding
              whether to activate it.
            </p>

            {resumedAt && (
              <div className="sfb-notice">
                <InfoOutlinedIcon style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} />
                <span>
                  We reopened the feed you started on{' '}
                  {new Date(resumedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}.
                </span>
              </div>
            )}

            <div className="sfb-intro-cta">
              <button
                type="button"
                className="sfb-btn sfb-btn--primary sfb-btn--lg"
                onClick={() => { setStep(0); scrollPageToTop(); }}
              >
                {resumedAt || hasAnyAnswer(answers) ? 'Continue my news feed' : 'Build my news feed'}
                <ArrowForwardIcon style={{ fontSize: 16 }} />
              </button>
              <p className="sfb-intro-fineprint">
                <ScheduleOutlinedIcon style={{ fontSize: 14, verticalAlign: '-2px', marginRight: 6 }} />
                Takes about 3 minutes. No feed will be activated without your approval.
              </p>
            </div>
          </div>

          <aside className="sfb-intro-aside">
            <p className="sfb-eyebrow">Example use case</p>
            <p className="sfb-intro-aside-label">Personalised brief</p>
            <p className="sfb-intro-aside-title">
              Media opportunities for a cybersecurity company
            </p>
          </aside>
        </div>
      </Shell>
    );
  }

  const STEP_META = [
    {
      eyebrow: 'Briefing worksheet',
      title: 'What should Alchemiq find for you?',
      lede: 'Tell us what this feed should cover and what you want to use it for.',
    },
    {
      eyebrow: 'Coverage and delivery',
      title: 'How should we deliver the results?',
      lede: 'Choose the result language, recipient and delivery frequency.',
    },
    {
      eyebrow: 'Review',
      title: 'Review and activate',
      lede: 'Check what Alchemiq will monitor and how the results will be delivered.',
    },
  ][step];

  return (
    <Shell save={{ state: saveState, onStartNew: requestStartNew, onRetry: flushSave }}>
      <div className="sfb-worksheet-head">
        <span className="sfb-eyebrow">{STEP_META.eyebrow}</span>
        <span className="sfb-step-count">Step {step + 1} of {TOTAL_STEPS}</span>
      </div>
      <div className="sfb-progress">
        <div className="sfb-progress-fill" style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }} />
      </div>

      <h1 className="sfb-title">{STEP_META.title}</h1>
      <p className="sfb-lede">{STEP_META.lede}</p>

      {formError && (
        <div className="sfb-notice">
          <InfoOutlinedIcon style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} />
          <span>{formError}</span>
        </div>
      )}

      {step === 0 && (
        <>
          <Section
            number="01"
            title="What is this feed about?"
            hint="Name the feed and add any background that will help us understand its subject."
          >
            {/* spec v8 #8/#9 — the feed name moved here from the review screen and
                is optional: if it is left blank we suggest one from Brief context. */}
            <Field label="Feed name" optional>
              <input
                className="sfb-input"
                value={answers.feedName}
                onChange={(e) => set('feedName', e.target.value)}
                placeholder="We will suggest one if you leave this blank."
              />
            </Field>

            <div className="sfb-grid">
              <Field label="Brief context" error={errors.organization}>
                <input
                  className={`sfb-input ${errors.organization ? 'sfb-input--error' : ''}`}
                  value={answers.organization}
                  onChange={(e) => set('organization', e.target.value)}
                  placeholder="e.g. a client, organisation, person, industry, market, topic, event, trend or issue"
                />
              </Field>
              <Field label="Relevant website" optional hint="Add a website if it provides useful background.">
                <input
                  className="sfb-input"
                  value={answers.relevantWebsite}
                  onChange={(e) => set('relevantWebsite', e.target.value)}
                  placeholder="https://example.com"
                />
              </Field>
            </div>

            <Field
              label="Background document"
              optional
              hint="A PDF, DOCX or text file we can read for context. Only our team can see it."
            >
              <label className={`sfb-upload ${uploadedFile ? 'sfb-upload--done' : ''}`}>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,.md,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ''; }}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
                {uploading ? (
                  <><span className="sfb-spinner" /> Reading your document…</>
                ) : uploadedFile ? (
                  <><CheckCircleOutlineIcon style={{ fontSize: 17 }} /> {uploadedFile.name} — replace</>
                ) : (
                  <><UploadFileOutlinedIcon style={{ fontSize: 17 }} /> Add a background document</>
                )}
              </label>
            </Field>

            {/* Adi review — a visitor with no document had no way to give us
                background at all. Typed notes reach the inference through the
                same path as an uploaded file's text. */}
            <Field
              label="Background notes"
              optional
              hint="Or type it here instead. Anything that helps us understand the subject — a brief, a positioning note, the competitors you watch."
            >
              <textarea
                className="sfb-textarea"
                value={answers.backgroundNotes}
                onChange={(e) => set('backgroundNotes', e.target.value)}
                placeholder="Paste or write any background that would help us."
              />
            </Field>
          </Section>

          <Section
            number="02"
            title="What should this feed help you do?"
            hint="Choose everything that applies — we use all of them to decide what to prioritise."
          >
            <div className="sfb-choices">
              {purposes.map((p) => (
                <Choice
                  key={p.key}
                  multi
                  selected={answers.purposes.includes(p.key)}
                  onClick={() => togglePurpose(p.key)}
                  title={p.label}
                />
              ))}
            </div>
            {errors.purpose && <div className="sfb-field-error">{errors.purpose}</div>}

            {/* spec v8 #16/#17 — "Something else" is only an answer once they say
                what it is. Before this it reached the inference as the literal
                words "something else". */}
            {answers.purposes.includes('other') && (
              <div className="sfb-choice-followup">
                <Field label="What should this feed help you do?" error={errors.purposeOther}>
                  <textarea
                    className={`sfb-textarea ${errors.purposeOther ? 'sfb-textarea--error' : ''}`}
                    value={answers.purposeOther}
                    onChange={(e) => set('purposeOther', e.target.value)}
                    placeholder="Describe what you want to get out of this feed."
                  />
                </Field>
              </div>
            )}
          </Section>

          <Section number="03" title="What should Alchemiq monitor?" hint="Plain English is fine — there is nothing technical to fill in.">
            <Field label="What would you like Alchemiq to find?" error={errors.whatToFind}>
              <textarea
                className={`sfb-textarea sfb-textarea--tall ${errors.whatToFind ? 'sfb-textarea--error' : ''}`}
                value={answers.whatToFind}
                onChange={(e) => set('whatToFind', e.target.value)}
                placeholder="A client, a company, a market, a research subject, an issue — describe it the way you would to a colleague."
              />
            </Field>
            <Field label="What should Alchemiq leave out?" optional>
              <textarea
                className="sfb-textarea"
                value={answers.whatToLeaveOut}
                onChange={(e) => set('whatToLeaveOut', e.target.value)}
                placeholder="Anything you do not want to see."
              />
            </Field>
          </Section>

          {/* spec v8 #21-24 — priority locations moved here from page 2, so
              everything describing WHAT to monitor now lives on one page. */}
          <Section
            number="04"
            title="What locations should we prioritise?"
            hint="Leave blank for global coverage. Naming places prioritises them; it does not exclude everywhere else."
          >
            <Field label="Priority locations" optional>
              <input
                className="sfb-input"
                value={answers.geographicCoverage}
                onChange={(e) => set('geographicCoverage', e.target.value)}
                placeholder="e.g. Germany and Austria"
              />
            </Field>
          </Section>

          <div className="sfb-actions">
            <button type="button" className="sfb-btn sfb-btn--secondary" onClick={() => { setStep(STEP_INTRO); scrollPageToTop(); }}>
              Back
            </button>
            <button
              type="button"
              className="sfb-btn sfb-btn--primary"
              onClick={() => saveAndAdvance(1)}
              disabled={advancing}
            >
              Continue <ArrowForwardIcon style={{ fontSize: 16 }} />
            </button>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <Section number="01" title="Who should receive it?" hint="One recipient. You can add more later with our team.">
            <div className="sfb-grid">
              <Field label="Recipient email" error={errors.recipientEmail}>
                <input
                  className={`sfb-input ${errors.recipientEmail ? 'sfb-input--error' : ''}`}
                  type="email"
                  value={answers.recipientEmail}
                  onChange={(e) => set('recipientEmail', e.target.value)}
                  placeholder="name@company.com"
                />
              </Field>
              <Field label="Result language" hint="We search every language we cover and deliver in this one.">
                <select
                  className="sfb-select"
                  value={answers.resultLanguage}
                  onChange={(e) => set('resultLanguage', e.target.value)}
                >
                  {languages.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          <Section number="02" title="How often should it arrive?" hint="You will only hear from us when there is something worth reading.">
            <div className="sfb-choices">
              {frequencies.map((f) => (
                <Choice
                  key={f.key}
                  selected={answers.frequency === f.key}
                  onClick={() => set('frequency', f.key)}
                  title={f.label}
                  // The days come from the server, per working week — the browser
                  // used to carry its own copy of the Sun–Thu assumption, which
                  // goes stale the moment the week is a choice.
                  description={f.days?.[workWeek]}
                />
              ))}
            </div>
            {errors.frequency && <div className="sfb-field-error">{errors.frequency}</div>}
          </Section>

          {/* Adi review — the schedule belongs to the client, not to us. */}
          <Section
            number="03"
            title="When should it arrive?"
            hint="We send at this time in your timezone, on your working days."
          >
            <div className="sfb-grid">
              <Field label="Your timezone" hint="Start typing a city or region.">
                <input
                  className="sfb-input"
                  list="sfb-timezones"
                  value={answers.scheduleTimeZone}
                  onChange={(e) => set('scheduleTimeZone', e.target.value)}
                  placeholder="e.g. Europe/London"
                />
                {/* A native datalist gives typeahead over 400+ zones with no
                    component library — this shell deliberately shares nothing
                    with the authenticated app's chrome. */}
                <datalist id="sfb-timezones">
                  {timeZones.map((z) => (
                    <option key={z.id} value={z.id}>{`${z.id} (${z.offset})`}</option>
                  ))}
                </datalist>
              </Field>
              <Field label="Delivery time">
                <select
                  className="sfb-select"
                  value={answers.scheduleHour}
                  onChange={(e) => set('scheduleHour', Number(e.target.value))}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Your working week">
              <div className="sfb-choices">
                {workWeeks.map((w) => (
                  <Choice
                    key={w.key}
                    selected={workWeek === w.key}
                    onClick={() => set('workWeek', w.key)}
                    title={w.label}
                  />
                ))}
              </div>
            </Field>

            <div className="sfb-delivery-time">
              <ScheduleOutlinedIcon style={{ fontSize: 16, flexShrink: 0 }} />
              <span>
                Delivered at{' '}
                <strong>{`${String(answers.scheduleHour).padStart(2, '0')}:00`}</strong>
                {answers.scheduleTimeZone ? <> in <strong>{answers.scheduleTimeZone}</strong></> : null}
                {selectedFrequency?.days?.[workWeek] ? <>, {selectedFrequency.days[workWeek]}</> : null}.
              </span>
            </div>
          </Section>

          <div className="sfb-actions">
            <button type="button" className="sfb-btn sfb-btn--secondary" onClick={() => { setStep(0); scrollPageToTop(); }}>Back</button>
            <button
              type="button"
              className="sfb-btn sfb-btn--primary"
              onClick={() => saveAndAdvance(2)}
              disabled={advancing}
            >
              Continue <ArrowForwardIcon style={{ fontSize: 16 }} />
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <Section number="01" title="Name your feed" hint="This is what your feed will be called.">
            {/* spec v8 #37/#40/#41 — shows what was entered on page 1, or the
                suggestion generated from Brief context, and cannot be empty. */}
            <Field label="Feed name" error={errors.feedName}>
              <input
                className={`sfb-input ${errors.feedName ? 'sfb-input--error' : ''}`}
                value={answers.feedName}
                onChange={(e) => set('feedName', e.target.value)}
              />
            </Field>
          </Section>

          <Section
            number="02"
            title="What Alchemiq will monitor"
            hint="Go back and change anything that does not look right."
            action={(
              <button type="button" className="sfb-section-action" onClick={() => { setStep(0); scrollPageToTop(); }}>
                <EditOutlinedIcon style={{ fontSize: 15 }} /> Edit
              </button>
            )}
          >
            <div>
              {monitorRows.map(([label, value]) => (
                <div className="sfb-review-item" key={label}>
                  <div className="sfb-review-label">{label}</div>
                  <div className="sfb-review-value">{value || '—'}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section
            number="03"
            title="Delivery"
            hint={`Sent at ${String(answers.scheduleHour).padStart(2, '0')}:00${answers.scheduleTimeZone ? ` in ${answers.scheduleTimeZone}` : ''}.`}
            action={(
              <button type="button" className="sfb-section-action" onClick={() => { setStep(1); scrollPageToTop(); }}>
                <EditOutlinedIcon style={{ fontSize: 15 }} /> Edit
              </button>
            )}
          >
            <div>
              {deliveryRows.map(([label, value]) => (
                <div className="sfb-review-item" key={label}>
                  <div className="sfb-review-label">{label}</div>
                  <div className="sfb-review-value">{value || '—'}</div>
                </div>
              ))}
            </div>
          </Section>

          <div className="sfb-actions">
            <button
              type="button"
              className="sfb-btn sfb-btn--secondary"
              onClick={() => { setStep(1); scrollPageToTop(); }}
              disabled={activating}
            >
              Back
            </button>
            {activating ? (
              <div className="sfb-activating">
                <span className="sfb-spinner" />
                Setting up your feed — this takes about a minute…
              </div>
            ) : (
              <button type="button" className="sfb-btn sfb-btn--primary" onClick={activate}>
                Activate my news feed <ArrowForwardIcon style={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        </>
      )}

      {/* spec v8 #56 — window.confirm is banned repo-wide; this is the builder's
          own confirm so the public shell keeps no dependency on the app chrome. */}
      <SfbConfirm
        open={confirmRestart}
        title="Start a new feed?"
        body="Your current answers will be replaced. We cannot recover them."
        cancelLabel="Keep editing"
        confirmLabel="Start a new feed"
        onCancel={() => setConfirmRestart(false)}
        onConfirm={startNewFeed}
      />
    </Shell>
  );
}

/** Navy bar + centred worksheet column, shared by every state of the page. */
function Shell({ children, save = null, wide = false }) {
  return (
    <div className="sfb-page">
      <div className="sfb-top-bar">
        <div className="sfb-top-bar-inner">
          <a href="/" aria-label="Alchemiq">
            <img
              src="/textOnlyLogoOrr-01.png"
              alt="Alchemiq"
              className="sfb-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </a>
          <span className="sfb-product-name">News Feed Builder</span>
          {save && (
            <span className="sfb-top-bar-actions">
              {/* spec v8 #55 */}
              <button type="button" className="sfb-top-bar-link" onClick={save.onStartNew}>
                Start a new feed
              </button>
              <SaveIndicator state={save.state} onRetry={save.onRetry} />
            </span>
          )}
        </div>
      </div>
      <main className={`sfb-main ${wide ? 'sfb-main--wide' : ''}`}>{children}</main>
    </div>
  );
}

/**
 * spec v8 #52/#53 — Saving… then Saved.
 *
 * Renders NOTHING while idle. The previous chip said "Saved in this browser"
 * permanently, which claimed a save before one had happened — and now that the
 * draft really is on the server, it would also be describing the wrong place.
 */
function SaveIndicator({ state, onRetry }) {
  if (state === 'saving') {
    return (
      <span className="sfb-top-bar-status">
        <span className="sfb-spinner sfb-spinner--tiny" />
        Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="sfb-top-bar-status">
        <CheckIcon fontSize="inherit" />
        Saved
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="sfb-top-bar-status sfb-top-bar-status--error">
        <InfoOutlinedIcon fontSize="inherit" />
        Not saved
        <button type="button" className="sfb-top-bar-link" onClick={onRetry}>Retry</button>
      </span>
    );
  }
  return null;
}

/**
 * The builder's own confirm. Native dialogs are banned repo-wide, and this shell
 * deliberately shares no components with the authenticated app so the public
 * route keeps rendering regardless of what changes there.
 */
function SfbConfirm({ open, title, body, cancelLabel, confirmLabel, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    cancelRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div className="sfb-modal-overlay" onClick={onCancel} role="presentation">
      <div
        className="sfb-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sfb-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="sfb-modal-title" id="sfb-modal-title">{title}</h2>
        <p className="sfb-modal-body">{body}</p>
        <div className="sfb-modal-actions">
          <button type="button" className="sfb-btn sfb-btn--secondary" ref={cancelRef} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className="sfb-btn sfb-btn--primary" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, hint, action, children }) {
  return (
    <section className="sfb-section">
      <div className="sfb-section-head">
        <span className="sfb-section-number">{number}</span>
        <h2 className="sfb-section-title">{title}</h2>
        {action}
        {hint && <p className="sfb-section-hint">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, optional, hint, error, children }) {
  return (
    <div className="sfb-field">
      <div className="sfb-label-row">
        <label className="sfb-label">{label}</label>
        {optional && <span className="sfb-optional">Optional</span>}
      </div>
      {children}
      {hint && !error && <div className="sfb-hint">{hint}</div>}
      {error && <div className="sfb-field-error">{error}</div>}
    </div>
  );
}

function Choice({ selected, onClick, title, description, multi = false }) {
  return (
    <button
      type="button"
      className={`sfb-choice ${selected ? 'sfb-choice--selected' : ''}`}
      onClick={onClick}
      // Several may be on at once in multi mode, so the control is a checkbox
      // rather than a pressed toggle — otherwise a screen reader announces a
      // radio group that allows more than one answer.
      role={multi ? 'checkbox' : undefined}
      aria-checked={multi ? selected : undefined}
      aria-pressed={multi ? undefined : selected}
    >
      <span className={`sfb-choice-radio ${multi ? 'sfb-choice-radio--multi' : ''}`} />
      <span>
        <span className="sfb-choice-title">{title}</span>
        {description && <span className="sfb-choice-desc">{description}</span>}
      </span>
    </button>
  );
}
